/**
 * PianoRollNode - 1D pattern with discrete value choices
 *
 * Data-agnostic pattern generator where:
 * - Rows represent available values (MIDI notes, colors, parameters)
 * - Columns represent steps in the sequence
 * - Each step has ONE selected row (one value per step)
 * - Output is a 1D array mapping selected rows to their values
 *
 * Endpoints:
 * - pattern (output): the mapped values
 * - rowValues (input/output): available values for each row
 * - selectedRows (input/output): which row is selected at each step (-1 = none)
 * - steps (input/output): number of steps
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { Dependencies } from '@nodes/Dependencies';
import { NodeDef } from '@runtime/IRuntime';
import { Payload } from '@graph/Payload';
import { params } from '@/nodes/TypedEndpoints';

const endpoints = params({
  pattern: { dataType: 'array', isOutput: true },
  rowValues: { dataType: 'array', isOutput: true, value: [0, 1, 2, 3, 4, 5, 6, 7] },
  selectedRows: { dataType: 'array', isOutput: true, value: [-1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1] },
  steps: {  isOutput: true, value: 16, min: 1, max: 128, step: 1 },
});

export class PianoRollNode extends BaseNode<typeof endpoints> {
  private highlightStep = -1;

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();

    // Ensure selectedRows matches steps
    const steps = this.params.steps.value;
    const selectedRows = this.params.selectedRows.value;

    if (selectedRows.length !== steps) {
      this.params.selectedRows.set(Array(steps).fill(-1));
    }
    this.emitPattern();
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    switch (endpointId) {
      case 'steps':
        this.resizeWidth(payload.value);
        break;
      case 'selectedRows':
      case 'rowValues':
        this.emitPattern();
        break;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Internal Accessors
  // ─────────────────────────────────────────────────────────────

  private get rowValues(): any[] {
    return this.params.rowValues.value;
  }

  private get selectedRows(): number[] {
    return this.params.selectedRows.value;
  }

  private set selectedRows(values: number[]) {
    this.params.selectedRows.set(values);
  }

  private get steps(): number {
    return this.params.steps.value;
  }

  private get rowCount(): number {
    return this.rowValues.length;
  }

  // ─────────────────────────────────────────────────────────────
  // Pattern Operations
  // ─────────────────────────────────────────────────────────────

  /**
   * Get the pattern as mapped values
   * Returns undefined for steps with no selection
   */
  get pattern(): (number | undefined)[] {
    return this.selectedRows.map(rowIndex => {
      if (rowIndex === -1) return undefined;
      return this.rowValues[rowIndex];
    });
  }

  getSelectedRow(step: number): number {
    if (step >= 0 && step < this.steps) {
      return this.selectedRows[step] ?? -1;
    }
    return -1;
  }

  setSelectedRow(step: number, rowIndex: number): void {
    if (step >= 0 && step < this.steps) {
      if (rowIndex >= -1 && rowIndex < this.rowCount) {
        const newSelectedRows = [...this.selectedRows];
        newSelectedRows[step] = rowIndex;
        this.selectedRows = newSelectedRows;
      }
    }
  }

  toggleCell(step: number, rowIndex: number): void {
    if (step >= 0 && step < this.steps && rowIndex >= 0 && rowIndex < this.rowCount) {
      const newSelectedRows = [...this.selectedRows];
      if (newSelectedRows[step] === rowIndex) {
        newSelectedRows[step] = -1; // Deselect
      } else {
        newSelectedRows[step] = rowIndex; // Select
      }
      this.selectedRows = newSelectedRows;
    }
  }

  getStepValue(step: number): number | undefined {
    const rowIndex = this.getSelectedRow(step);
    if (rowIndex === -1) return undefined;
    return this.rowValues[rowIndex];
  }

  clear(): void {
    this.selectedRows = Array(this.steps).fill(-1);
  }

  /**
   * Set pattern from MIDI notes (convenience method)
   * Automatically maps notes to row indices
   */
  setFromNotes(notes: (number | undefined)[]): void {
    const newSelectedRows = notes.map(note => {
      if (note === undefined) return -1;
      const idx = this.rowValues.indexOf(note);
      return idx >= 0 ? idx : -1;
    });

    // Resize if needed
    if (newSelectedRows.length !== this.steps) {
      this.params.steps.set(newSelectedRows.length);
    }
    this.selectedRows = newSelectedRows;
  }

  // ─────────────────────────────────────────────────────────────
  // Animation
  // ─────────────────────────────────────────────────────────────

  setHighlightStep(step: number): void {
    this.highlightStep = step % this.steps;
  }

  get currentHighlightStep(): number {
    return this.highlightStep;
  }

  // ─────────────────────────────────────────────────────────────
  // Private
  // ─────────────────────────────────────────────────────────────

  private resizeWidth(newWidth: number): void {
    if (newWidth < 1) return;
    const current = this.selectedRows;

    if (newWidth > current.length) {
      this.params.selectedRows.set([...current, ...Array(newWidth - current.length).fill(-1)]);
    } else if (newWidth < current.length) {
      this.params.selectedRows.set(current.slice(0, newWidth));
    }
    this.emitPattern();
  }

  private emitPattern(): void {
    this.params.pattern.set(this.pattern);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const pianoRollNodeDef: NodeDef<PianoRollNode> = {
  type: 'PianoRoll',
  info: '1D pattern editor with discrete value choices per step. Each column selects one row value.',
  domain: Domain.Data,
  endpoints,
  ctor: PianoRollNode
};
